import { getItem, storeItem, removeItem } from './localStorage';
import { darken, lighten } from '@mui/material';
import { COLOR_VARIABLES } from 'theme/colors';
import { DEFAULTTHEME } from 'theme/defaultTheme';

export enum THEME_TYPE {
    THEME_GM = 1,
    THEME_GCM
}

export const getThemeType = (): THEME_TYPE => {
    return process.env.REACT_APP_THEME === 'GCM' ? THEME_TYPE.THEME_GCM : THEME_TYPE.THEME_GM;
};

export type UserTheme = {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    primaryText: string;

    secondary: string;
    secondaryDark: string;
    secondaryLight: string;
    secondaryText: string;

    disabled: string;
    disabledBgColor: string;
    dashboardBgColor: string;

    successColor: string;
    warningColor: string;
    errorColor: string;
    background: string;
    dashboardBgImage: string;
    dashboardImage: string;
    organizationLogo: string;
    LoginBackgroundImage: string;

    stateOpen: string;
    stateAssigned: string;
    stateResourced: string;
    stateOngoing: string;
    stateDone: string;
    stateCancelled: string;
    statePaused: string;
    stateFailed: string;

    colorWhite: string;
    colorLightGray: string;
    colorLightBlue?: string; // Optional for GCM theme
};

export const THEME_STORAGE = {
    primary: 'primary',
    primaryDark: 'primaryDark',
    primaryLight: 'primaryLight',
    primaryText: 'primaryText',

    secondary: 'secondary',
    secondaryDark: 'secondaryDark',
    secondaryLight: 'secondaryLight',
    secondaryText: 'secondaryText',

    disabled: 'disabled',
    disabledBgColor: 'disabledBgColor',
    dashboardBgColor: 'dashboardBgColor',

    successColor: 'successColor',
    warningColor: 'warningColor',
    errorColor: 'errorColor',

    background: 'primaryBackground',
    dashboardBgImage: 'dashboardBgImage',
    dashboardImage: 'dashboardImage',
    organizationLogo: 'organizationLogo',
    LoginBackgroundImage: 'LoginBackgroundImage',

    stateOpen: 'stateOpen',
    stateAssigned: 'stateAssigned',
    stateResourced: 'stateResourced',
    statePaused: 'statePaused',
    stateOngoing: 'stateOngoing',
    stateDone: 'stateDone',
    stateCancelled: 'stateCancelled',
    stateFailed: 'stateFailed'
};

export const getDashboardBgImage = () => {
    return getThemeItem(THEME_STORAGE.dashboardBgImage, DEFAULTTHEME.dashboardBgImage);
};

export const getDashboardImage = () => {
    return getThemeItem(THEME_STORAGE.dashboardImage, DEFAULTTHEME.dashboardImage);
};

export const getOrganizationLogo = () => {
    return getThemeItem(THEME_STORAGE.organizationLogo, DEFAULTTHEME.organizationLogo);
};

export const getLoginBgImage = () => {
    return getThemeItem(THEME_STORAGE.LoginBackgroundImage, DEFAULTTHEME.LoginBackgroundImage);
};

export const getThemeItem = (item: string, default_value: string): string => {
    let initValue = getItem(item);
    return initValue ? initValue : default_value;
};

export const getSetThemeItem = (item: string, default_value: string, newvalue?: string): string => {
    if (newvalue && newvalue.length > 5) {
        storeItem(item, newvalue);
        return newvalue;
    } else {
        removeItem(item);
    }
    return default_value;
};

export const getSetThemeItemDarken = (
    item: string,
    default_value: string,
    newvalue?: string
): string => {
    if (newvalue && newvalue.length > 5) {
        storeItem(item, darken(newvalue, 0.2));
        return darken(newvalue, 0.2);
    } else {
        removeItem(item);
    }
    return darken(default_value, 0.2);
};
export const getSetThemeItemLighten = (
    item: string,
    default_value: string,
    newvalue?: string
): string => {
    if (newvalue && newvalue.length > 5) {
        storeItem(item, lighten(newvalue, 0.2));
        return lighten(newvalue, 0.2);
    } else {
        removeItem(item);
    }
    return lighten(default_value, 0.2);
};

export const updateUserTheme = (newTheme?: any): UserTheme => {
    return {
        primary: getSetThemeItem(
            THEME_STORAGE.primary,
            DEFAULTTHEME.primary,
            newTheme?.PrimaryColor
        ),
        primaryDark: getSetThemeItemDarken(
            THEME_STORAGE.primaryDark,
            DEFAULTTHEME.primaryDark,
            newTheme?.PrimaryColor
        ),
        primaryLight: getSetThemeItemLighten(
            THEME_STORAGE.primaryLight,
            DEFAULTTHEME.primaryLight,
            newTheme?.PrimaryColor
        ),
        primaryText: getSetThemeItem(
            THEME_STORAGE.primaryText,
            DEFAULTTHEME.primaryText,
            newTheme?.PrimaryTextColor
        ),

        secondary: getSetThemeItem(
            THEME_STORAGE.secondary,
            DEFAULTTHEME.secondary,
            newTheme?.SecondaryColor
        ),
        secondaryDark: getSetThemeItemDarken(
            THEME_STORAGE.secondaryDark,
            DEFAULTTHEME.secondaryDark,
            newTheme?.SecondaryColor
        ),
        secondaryLight: getSetThemeItemLighten(
            THEME_STORAGE.secondaryLight,
            DEFAULTTHEME.secondaryLight,
            newTheme?.SecondaryColor
        ),
        secondaryText: getSetThemeItem(
            THEME_STORAGE.secondaryText,
            DEFAULTTHEME.secondaryText,
            newTheme?.SecondaryTextColor
        ),
        disabledBgColor: getSetThemeItem(
            THEME_STORAGE.disabledBgColor,
            DEFAULTTHEME.disabledBgColor,
            newTheme?.DisabledColor
        ),
        disabled: getSetThemeItem(
            THEME_STORAGE.disabled,
            DEFAULTTHEME.disabled,
            newTheme?.DisabledColor
        ),
        dashboardBgColor: getSetThemeItem(
            THEME_STORAGE.dashboardBgColor,
            DEFAULTTHEME.dashboardBgColor,
            newTheme?.DashboardBgColor
        ),

        successColor: getSetThemeItem(
            THEME_STORAGE.successColor,
            DEFAULTTHEME.successColor,
            newTheme?.SuccessColor
        ),
        warningColor: getSetThemeItem(
            THEME_STORAGE.warningColor,
            DEFAULTTHEME.warningColor,
            newTheme?.WarningColor
        ),
        errorColor: getSetThemeItem(
            THEME_STORAGE.errorColor,
            DEFAULTTHEME.errorColor,
            newTheme?.ErrorColor
        ),

        background: getSetThemeItem(
            THEME_STORAGE.background,
            DEFAULTTHEME.background,
            newTheme?.BackgroundColor
        ),
        dashboardBgImage: getSetThemeItem(
            THEME_STORAGE.dashboardBgImage,
            DEFAULTTHEME.dashboardBgImage,
            newTheme?.DashboardBgImage
        ),
        dashboardImage: getSetThemeItem(
            THEME_STORAGE.dashboardImage,
            DEFAULTTHEME.dashboardImage,
            newTheme?.DashboardImage
        ),

        organizationLogo: getSetThemeItem(
            THEME_STORAGE.organizationLogo,
            DEFAULTTHEME.organizationLogo,
            newTheme?.OrganizationLogo
        ),
        LoginBackgroundImage: getSetThemeItem(
            THEME_STORAGE.LoginBackgroundImage,
            DEFAULTTHEME.LoginBackgroundImage,
            newTheme?.LoginBackgroundImage
        ),

        stateOpen: DEFAULTTHEME.stateOpen,
        stateAssigned: DEFAULTTHEME.stateAssigned,
        stateResourced: DEFAULTTHEME.stateResourced,
        stateOngoing: DEFAULTTHEME.stateOngoing,
        stateDone: DEFAULTTHEME.stateDone,
        stateCancelled: DEFAULTTHEME.stateCancelled,
        stateFailed: DEFAULTTHEME.stateFailed,
        statePaused: DEFAULTTHEME.statePaused,

        colorWhite: COLOR_VARIABLES.colors_white,
        colorLightGray: COLOR_VARIABLES.colors_lightgray,
        colorLightBlue: COLOR_VARIABLES.colors_light_blue
    };
};

export const getStoredUserTheme = (): UserTheme => {
    return {
        primary: getThemeItem(THEME_STORAGE.primary, DEFAULTTHEME.primary),
        primaryDark: getThemeItem(THEME_STORAGE.primaryDark, DEFAULTTHEME.primaryDark),
        primaryLight: getThemeItem(THEME_STORAGE.primaryLight, DEFAULTTHEME.primaryLight),
        primaryText: getThemeItem(THEME_STORAGE.primaryText, DEFAULTTHEME.primaryText),

        secondary: getThemeItem(THEME_STORAGE.secondary, DEFAULTTHEME.secondary),
        secondaryDark: getThemeItem(THEME_STORAGE.secondaryDark, DEFAULTTHEME.secondaryDark),
        secondaryLight: getThemeItem(THEME_STORAGE.secondaryLight, DEFAULTTHEME.secondaryLight),
        secondaryText: getThemeItem(THEME_STORAGE.secondaryText, DEFAULTTHEME.secondaryText),

        disabledBgColor: getSetThemeItem(
            THEME_STORAGE.disabledBgColor,
            DEFAULTTHEME.disabledBgColor
        ),
        disabled: getSetThemeItem(THEME_STORAGE.disabled, DEFAULTTHEME.disabled),
        dashboardBgColor: getThemeItem(
            THEME_STORAGE.dashboardBgColor,
            DEFAULTTHEME.dashboardBgColor
        ),
        successColor: getThemeItem(THEME_STORAGE.successColor, DEFAULTTHEME.successColor),
        warningColor: getThemeItem(THEME_STORAGE.warningColor, DEFAULTTHEME.warningColor),
        errorColor: getSetThemeItem(THEME_STORAGE.errorColor, DEFAULTTHEME.errorColor),

        background: getSetThemeItem(THEME_STORAGE.background, DEFAULTTHEME.background),
        dashboardBgImage: getThemeItem(
            THEME_STORAGE.dashboardBgImage,
            DEFAULTTHEME.dashboardBgImage
        ),
        dashboardImage: getThemeItem(THEME_STORAGE.dashboardImage, DEFAULTTHEME.dashboardImage),

        organizationLogo: getThemeItem(
            THEME_STORAGE.organizationLogo,
            DEFAULTTHEME.organizationLogo
        ),
        LoginBackgroundImage: getThemeItem(
            THEME_STORAGE.LoginBackgroundImage,
            DEFAULTTHEME.LoginBackgroundImage
        ),

        stateOpen: DEFAULTTHEME.stateOpen,
        stateAssigned: DEFAULTTHEME.stateAssigned,
        stateResourced: DEFAULTTHEME.stateResourced,
        stateOngoing: DEFAULTTHEME.stateOngoing,
        stateDone: DEFAULTTHEME.stateDone,
        stateCancelled: DEFAULTTHEME.stateCancelled,
        stateFailed: DEFAULTTHEME.stateFailed,
        statePaused: DEFAULTTHEME.statePaused,

        colorWhite: COLOR_VARIABLES.colors_white,
        colorLightGray: COLOR_VARIABLES.colors_lightgray,
        colorLightBlue: COLOR_VARIABLES.colors_light_blue
    };
};
